import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { format, subDays } from 'date-fns'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'

import { formatOrderTypeForReceipt } from '@/lib/printCustomerReceipt'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'
import type { Order } from '@/types'
import { cn } from '@/lib/utils'

type RangeKey = 'today' | 'yesterday' | 'week'

function orderHistoryFilters(
  orderType: string,
  range: RangeKey
): { date_from: string; date_to: string; order_type: string; per_page: number } {
  const today = new Date()
  const todayStr = format(today, 'yyyy-MM-dd')
  if (range === 'today') {
    return { date_from: todayStr, date_to: todayStr, order_type: orderType, per_page: 50 }
  }
  if (range === 'yesterday') {
    const y = format(subDays(today, 1), 'yyyy-MM-dd')
    return { date_from: y, date_to: y, order_type: orderType, per_page: 50 }
  }
  const from = format(subDays(today, 6), 'yyyy-MM-dd')
  return { date_from: from, date_to: todayStr, order_type: orderType, per_page: 80 }
}

export type CounterOrderHistoryBodyProps = {
  orderType: string
  formatCurrency: (n: number) => string
  onSelectOrder: (order: Order) => void
}

/**
 * Headless body for the Order history section. Rendered inside a popover
 * by `CounterRailToolbar`.
 */
export function CounterOrderHistoryBody({
  orderType,
  formatCurrency,
  onSelectOrder,
}: CounterOrderHistoryBodyProps) {
  const [range, setRange] = useState<RangeKey>('today')
  const { raw: orderTypesRaw } = useEnabledOrderTypes()
  const filters = useMemo(() => orderHistoryFilters(orderType, range), [orderType, range])

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['counterOrderHistory', filters],
    queryFn: async () => {
      const res = await apiClient.getOrders(filters)
      if (res.success === false && !Array.isArray(res.data)) {
        throw new Error(res.message || 'Failed to load orders')
      }
      return Array.isArray(res.data) ? res.data : []
    },
  })

  const rows = data ?? []

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-snug text-muted-foreground">
        Past orders for the same mode as your checkout toggle. Pick a date range, then tap a row
        to open or view details.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ['today', 'Today'],
            ['yesterday', 'Yesterday'],
            ['week', '7 days'],
          ] as const
        ).map(([k, label]) => (
          <Button
            key={k}
            type="button"
            size="sm"
            variant={range === k ? 'default' : 'outline'}
            className="h-8 rounded-md px-2.5 text-xs"
            onClick={() => setRange(k)}
          >
            {label}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-2 text-xs text-muted-foreground"
          onClick={() => void refetch()}
        >
          Refresh
        </Button>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-md border border-border/70 bg-muted/15">
        {isFetching && (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
        )}
        {isError && (
          <div className="px-3 py-4 text-center text-xs text-destructive">
            Could not load orders.
          </div>
        )}
        {!isFetching && !isError && rows.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            No orders in this range.
          </div>
        )}
        {!isFetching &&
          rows.map((o) => (
            <button
              key={o.id}
              type="button"
              className={cn(
                'flex w-full flex-col gap-1 border-b border-border/50 px-3 py-2.5 text-left text-sm last:border-0',
                'hover:bg-background/80 focus-visible:bg-background/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
              )}
              onClick={() => onSelectOrder(o)}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  #{o.order_number}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {format(new Date(o.created_at), 'MMM d · h:mm a')}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>
                  <span className="text-foreground/80">{formatOrderTypeForReceipt(o.order_type, displayLabelForOrderType(o.order_type, orderTypesRaw))}</span>
                  <span className="mx-1 text-muted-foreground/50">·</span>
                  <span className="capitalize">{o.status.replace('_', ' ')}</span>
                </span>
                <span className="shrink-0 tabular-nums font-medium text-foreground">
                  {formatCurrency(o.total_amount)}
                </span>
              </div>
            </button>
          ))}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        For aggregates and exports, open{' '}
        <Link
          to="/admin/reports"
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          Admin → Reports
        </Link>
        .
      </p>
    </div>
  )
}
